/**
 * Language store (BUILD_SPEC.md §14).
 *
 * Authoritative source for the active locale. Persisted to localStorage
 * (§14.1). Every change runs three side effects in lockstep:
 *   1. i18next.changeLanguage updates UI strings.
 *   2. applyDocumentLocale flips <html dir> and <html lang>.
 *   3. The body font family swaps via the lang-attribute rule in
 *      styles/index.css.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { applyDocumentLocale, i18n, type Locale } from "@/lib/i18n";

interface LangState {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  toggle: () => void;
}

/** Apply locale to both i18next and the document. */
function applyLocale(locale: Locale): void {
  void i18n.changeLanguage(locale);
  applyDocumentLocale(locale);
}

export const useLangStore = create<LangState>()(
  persist(
    (set, get) => ({
      locale: "ar",
      setLocale: (locale) => {
        set({ locale });
        applyLocale(locale);
      },
      toggle: () => {
        const next: Locale = get().locale === "ar" ? "en" : "ar";
        set({ locale: next });
        applyLocale(next);
      },
    }),
    {
      name: "azzam.lang",
      onRehydrateStorage: () => (state) => {
        if (state) applyLocale(state.locale);
      },
    },
  ),
);
